/**
 * Evaluates whether an answer matches a field's disqualifier config.
 * When true, the visitor should be sent to the dead-end screen (no appointment booking).
 * Comparisons are case-insensitive for string values; types/operators normalized to avoid casing issues.
 *
 * @param {Object} field - Qualification field with optional disqualifier: { type, disqualifier?: { type, values?, value?, operator? } }
 * @param {string|number|*} value - The answer value (as stored: string for select/text, number for number, 1|0 for checkbox, JSON string for multi_select)
 * @returns {boolean} true if the answer matches the disqualifier (visitor should be disqualified)
 */
export function evaluateDisqualifier(field, value) {
    if (!field || !field.disqualifier) return false;
    const d = field.disqualifier;
    const type = String(field.type || '').toLowerCase();

    if (type === 'select') {
        const dType = String(d.type || '').toLowerCase();
        if (dType !== 'options' || !Array.isArray(d.values) || d.values.length === 0) return false;
        const strVal = (value != null ? String(value).trim() : '').toLowerCase();
        return d.values.some((v) => String(v).trim().toLowerCase() === strVal);
    }

    if (type === 'multi_select') {
        const dType = String(d.type || '').toLowerCase();
        if (dType !== 'options' || !Array.isArray(d.values) || d.values.length === 0) return false;
        let selected = [];
        try {
            selected = typeof value === 'string' ? JSON.parse(value) : (Array.isArray(value) ? value : []);
        } catch (_) {
            return false;
        }
        if (!Array.isArray(selected)) return false;
        const dValuesLower = d.values.map((v) => String(v).trim().toLowerCase());
        return selected.some((v) => dValuesLower.includes(String(v).trim().toLowerCase()));
    }

    if (type === 'number') {
        const dType = String(d.type || '').toLowerCase();
        if (dType !== 'threshold' || d.operator == null) return false;
        const num = typeof value === 'number' ? value : parseFloat(value);
        if (Number.isNaN(num)) return false;
        const threshold = typeof d.value === 'number' ? d.value : parseFloat(d.value);
        if (Number.isNaN(threshold)) return false;
        const op = String(d.operator).toLowerCase();
        switch (op) {
            case 'gt': return num > threshold;
            case 'gte': return num >= threshold;
            case 'lt': return num < threshold;
            case 'lte': return num <= threshold;
            case 'eq': return num === threshold;
            default: return false;
        }
    }

    if (type === 'text' || type === 'textarea') {
        const strVal = (value != null ? String(value).trim() : '').toLowerCase();
        const matchVal = (d.value != null ? String(d.value).trim() : '').toLowerCase();
        const dType = String(d.type || '').toLowerCase();
        if (dType === 'contains') return matchVal.length > 0 && strVal.includes(matchVal);
        if (dType === 'equals') return matchVal.length > 0 && strVal === matchVal;
        return false;
    }

    if (type === 'checkbox') {
        const dType = String(d.type || '').toLowerCase();
        if (dType !== 'equals') return false;
        const configVal = normalizeCheckboxValue(d.value);
        if (configVal !== 'yes' && configVal !== 'no') return false;
        const answer = normalizeCheckboxValue(value);
        return answer !== null && answer === configVal;
    }

    return false;
}

function normalizeCheckboxValue(v) {
    if (v === true || v === 1 || v === '1') return 'yes';
    if (v === false || v === 0 || v === '0') return 'no';
    const s = (v != null ? String(v).trim() : '').toLowerCase();
    if (s === 'yes' || s === 'true') return 'yes';
    if (s === 'no' || s === 'false') return 'no';
    return null;
}

/**
 * Counts how many fields with a disqualifier have an answer in listDataQuestion that matches their disqualifier.
 * Use with min_disqualifier_matches: disqualify only when this count >= min (e.g. 2 = "need 2 disqualifying answers").
 *
 * @param {Array} fields - List of qualification fields (with optional disqualifier)
 * @param {Object} listDataQuestion - Map of field id -> answer value (e.g. { 1: "No", 2: "example.com" })
 * @returns {number}
 */
export function countDisqualifierMatches(fields, listDataQuestion) {
    if (!Array.isArray(fields) || !listDataQuestion || typeof listDataQuestion !== 'object') return 0;
    let count = 0;
    for (const field of fields) {
        if (!field || !field.disqualifier) continue;
        const id = field.id;
        const value = listDataQuestion[id] ?? listDataQuestion[String(id)];
        if (value === undefined) continue;
        if (evaluateDisqualifier(field, value)) count++;
    }
    return count;
}

/**
 * Returns true if the lead should be disqualified now: the current answer is disqualifying and the total
 * number of disqualifier matches across all fields is >= minMatches (default 1).
 *
 * @param {Array} fields - All qualification fields
 * @param {Object} listDataQuestion - All answers so far (including the one just submitted)
 * @param {number} minMatches - Require at least this many disqualifying answers (default 1)
 * @returns {boolean}
 */
export function shouldDisqualifyNow(fields, listDataQuestion, minMatches = 1) {
    const n = Math.max(1, parseInt(minMatches, 10) || 1);
    return countDisqualifierMatches(fields, listDataQuestion) >= n;
}
